import { type Action, type StoreActionApi } from 'react-sweet-state';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { type ExperienceNameType } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	type ChatStorePrivateState,
	type StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { setConversationData } from './setConversationData';
import { storeErrorHandler } from './store-error-handler';
import type { ActionErrorHandler, ChatStoreContainerProps } from './types';

/**
 * The dispatchable action creator produced by {@link createSafeAction}.
 *
 * Given the same argument list as the wrapped action factory, it returns a
 * react-sweet-state `Action` whose result is `Promise<R>` (defaults to
 * `Promise<void>`).
 *
 * Exported so call sites can add the explicit type annotation required by
 * TypeScript's `--isolatedDeclarations`.
 */
export type SafeAction<TArgs extends unknown[], R = void> = (
	...args: TArgs
) => Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<R>>;

const withSiteGuard = (
	api: StoreActionApi<ChatStorePrivateState>,
	containerProps: ChatStoreContainerProps,
	actionLoggableName: StoreActionLoggableName,
): StoreActionApi<ChatStorePrivateState> => {
	const actionSiteId = containerProps.siteId;

	if (!actionSiteId) {
		return api;
	}

	const isFromPreviousSite = () => {
		const { hydratedSiteId } = api.getState();
		return hydratedSiteId !== undefined && hydratedSiteId !== actionSiteId;
	};

	const dropped = (what: string) => {
		addSentryBreadcrumb(
			`createSafeAction: dropped ${actionLoggableName} ${what} from a previous site`,
		);
	};

	return {
		getState: api.getState,
		setState: (partialState) => {
			if (isFromPreviousSite()) {
				dropped('state update');
				return;
			}

			api.setState(partialState);
		},
		dispatch: ((actionThunk) => {
			if (isFromPreviousSite()) {
				dropped('dispatch');
				return undefined;
			}

			return api.dispatch(actionThunk);
		}) as StoreActionApi<ChatStorePrivateState>['dispatch'],
	};
};

/**
 * Action Factory
 * Adds custom _middlewares_ to handle common cases and unexpected errors.
 * This is a hack to attach common logic to all actions.
 * It should not use the store action dispatcher. Instead, should call the actionCallback directly.
 *
 *
 * To add invocation-level error handling (`onError`):
 *
 * 1. Define actionargs type with `onError` using `OnErrorCallback`:
 *    ```ts
 *    type MyActionArgs = {
 *      myParam: string;
 *      onError?: OnErrorCallback<MyActionArgs>;
 *    };
 *    ```
 *
 * 2. Wire up `actionErrorHandler` to delegate to `onError`:
 *    ```ts
 *    createSafeAction(name, actionFn, {
 *      actionErrorHandler: (context) => {
 *        const { onError, ...args } = context.args?.[0] ?? {};
 *			if (onError) {
 *				return onError({ ...context, args });
 *			}
 *			// default action level error handling
 *			return { proceedToDefaultHandling: true };
 *      },
 *    });
 *    ```
 *
 */
export const createSafeAction =
	<TArgs extends unknown[], R = void>(
		actionLoggableName: StoreActionLoggableName,
		actionHOF: (...args: TArgs) => Action<ChatStorePrivateState, ChatStoreContainerProps, unknown>,
		props?: {
			experienceName?: (args: TArgs) => ExperienceNameType;
			getRateLimitState?: (args: TArgs) => { isDeepResearchAttempt?: boolean };
			actionErrorHandler?: ActionErrorHandler<TArgs>;
		},
	) =>
	(...args: TArgs): Action<ChatStorePrivateState, ChatStoreContainerProps, Promise<R>> =>
	async (api: StoreActionApi<ChatStorePrivateState>, containerProps: ChatStoreContainerProps) => {
		const guardedApi = fg('rovo_chat_site_switch_reset')
			? withSiteGuard(api, containerProps, actionLoggableName)
			: api;

		/*
		  Add custom logic here
		 */
		// General try/catch statement to handle unexpected errors
		try {
			addSentryBreadcrumb(`createSafeAction: executing ${actionLoggableName}`);
			// Here we call the action callback
			// Same as dispatch(actionHOF)
			const action = actionHOF(...args);
			// Here we execute the action i.e. what happens internally when the action is dispatched
			const result = await action(guardedApi, containerProps);
			return result as R;
		} catch (error: unknown) {
			storeErrorHandler({
				args,
				api: guardedApi,
				actionLoggableName,
				error,
				containerProps,
				setConversationData,
				experienceName: props?.experienceName,
				getRateLimitState: props?.getRateLimitState,
				actionErrorHandler: props?.actionErrorHandler,
			});
			return undefined as R;
		}
	};
